// Bitcube landing section (Shopify): reveals, videos, screens, gallery + lightbox, product form.
// Port of site-v2/app.js. Everything is scoped to the section root (#bitcube-landing) and every class
// carries the bc- prefix. The firmware demo lives in bitcube-live.js, imported through the asset map
// the section injects (window.BITCUBE_ASSETS), so the theme's CDN URL is used.

const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const canHover = matchMedia('(hover: hover)').matches;
// Videos are poster-first: their <source>s carry data-src and get src (then load()) only when needed.
const attach = v => {
  if (v.dataset.bcAttached) return;
  v.dataset.bcAttached = '1';
  v.querySelectorAll('source[data-src]').forEach(s => { s.src = s.dataset.src; });
  v.load();
};
const play = v => { attach(v); const p = v.play(); if (p && p.catch) p.catch(() => {}); };

// ------------------------------------------------------------------ money
// The price always comes from Liquid (cents, presentment currency). Client-side totals are formatted
// with Shopify.formatMoney when the theme provides it, else with the format string; either result is
// checked against a sample Liquid rendered with the same filter, and the sample's own separators and
// symbol placement win if they disagree (e.g. a market currency different from the shop format).
function formatWithFormat(cents, format) {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    try { return String(window.Shopify.formatMoney(cents, format)); } catch (e) { /* fall through */ }
  }
  const group = (n, decimals, thousands, decimal) => {
    const [int, frac] = (n / 100).toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  const m = /\{\{\s*(\w+)\s*\}\}/.exec(format);
  if (!m) return null;
  const kinds = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
  };
  const k = kinds[m[1]];
  if (!k) return null;
  return format.replace(m[0], group(cents, ...k));
}
function fromSample(sample) {
  // sample = 123456789 cents rendered by Liquid, e.g. "€1.234.567,89 EUR", "$1,234,567.89 USD", "¥1,234,568 JPY"
  const m = /1(\D?)234(\D?)56([78])(?:(\D)(89))?/.exec(sample);
  if (!m) return null;
  const thousands = m[1], decimals = m[5] ? 2 : 0, decimal = m[4] || '.';
  const pre = sample.slice(0, m.index), post = sample.slice(m.index + m[0].length);
  return cents => {
    const [int, frac] = (cents / 100).toFixed(decimals).split('.');
    return pre + int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '') + post;
  };
}
function moneyFormatter(el) {
  const strip = s => (s || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  const format = strip(el.dataset.moneyFormat);
  const sample = strip(el.dataset.moneySample);
  const bySample = fromSample(sample);
  const viaFormat = format ? formatWithFormat(123456789, format) : null;
  if (viaFormat && (!sample || strip(viaFormat) === sample)) return cents => strip(formatWithFormat(cents, format));
  if (bySample) return bySample;
  if (viaFormat) return cents => strip(formatWithFormat(cents, format));
  return cents => (cents / 100).toFixed(2);
}

// ------------------------------------------------------------------ image swap without flicker
// Load + decode the next image off-screen while the current one stays visible, then swap in one
// frame (MDN: HTMLImageElement.decode()). A token drops stale requests when taps come quickly.
const warmed = new Map();
function warm(src, srcset, sizes) {
  const key = srcset || src;
  if (warmed.has(key)) return warmed.get(key);
  const im = new Image();
  im.decoding = 'async';
  if (srcset) { im.sizes = sizes || '100vw'; im.srcset = srcset; }
  im.src = src;
  const p = (im.decode ? im.decode() : new Promise((res, rej) => { im.onload = res; im.onerror = rej; }))
    .catch(() => new Promise(res => (im.complete ? res() : (im.onload = im.onerror = res))))
    .then(() => im);
  warmed.set(key, p);
  return p;
}

function init(root) {
  if (!root || root.dataset.bcInit) return;
  root.dataset.bcInit = '1';
  root.classList.add('bc-js');
  const $ = sel => root.querySelector(sel);
  const $$ = sel => [...root.querySelectorAll(sel)];

  // ---------------------------------------------------------------- reveals
  $$('[data-bc-stagger]').forEach(group => {
    [...group.querySelectorAll(':scope > [data-bc-reveal]')].forEach((el, i) => el.style.setProperty('--bc-i', String(i % 8)));
  });
  const revealIO = new IntersectionObserver(entries => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      e.target.classList.add('bc-in');
      revealIO.unobserve(e.target);
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  $$('[data-bc-reveal]').forEach(el => revealIO.observe(el));

  // ---------------------------------------------------------------- in-page links (#bc-…)
  root.addEventListener('click', e => {
    const a = e.target.closest('a[href^="#bc-"]');
    if (!a) return;
    const target = root.querySelector(a.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    e.stopPropagation();   // the theme's own anchor-scroll handler would otherwise override ours
    // "Try me": bring the demo stage and its control row fully into view, not the section heading.
    const live = a.getAttribute('href') === '#bc-try' && target.querySelector('.bc-live');
    if (live) {
      const r = live.getBoundingClientRect(), header = 110;
      const room = innerHeight - header;
      const top = scrollY + r.top - header - Math.max(0, (room - r.height) / 2);
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
    } else {
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
    history.replaceState(null, '', a.getAttribute('href'));
  });

  // ---------------------------------------------------------------- videos: file on approach, play only in view
  // Nothing video-related is requested until the clip is ~1.5 viewports away; phones get the 720 px files
  // (<source media>). The gallery tile uses the same URLs and attaches them on hover (touch: in view).
  const videoIO = new IntersectionObserver(entries => {
    for (const e of entries) {
      const v = e.target;
      if (e.isIntersecting && !reduceMotion) play(v); else v.pause();
    }
  }, { threshold: 0.35 });
  const nearIO = new IntersectionObserver(entries => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      nearIO.unobserve(e.target);
      e.target.preload = 'auto';
      attach(e.target);
    }
  }, { rootMargin: '0px 0px 40% 0px' });
  // Watch only after the page has finished loading, so the clip never competes with the first screen.
  const afterLoad = fn => (document.readyState === 'complete' ? fn() : addEventListener('load', fn, { once: true }));
  $$('video[data-bc-inview]').forEach(v => { videoIO.observe(v); if (!reduceMotion) afterLoad(() => nearIO.observe(v)); });
  $$('video[data-bc-hoverplay]').forEach(v => {
    if (canHover) {
      const tile = v.closest('.bc-wild-tile');
      tile.addEventListener('mouseenter', () => { if (!reduceMotion) play(v); });
      tile.addEventListener('mouseleave', () => v.pause());
    } else {
      videoIO.observe(v);
    }
  });

  // ---------------------------------------------------------------- screens: hover swaps
  if (canHover) $$('.bc-device img[data-hover]').forEach(img => {
    const a = img.getAttribute('src'), b = img.dataset.hover;
    const tile = img.closest('.bc-screen-tile');
    let preloaded = false;
    tile.addEventListener('mouseenter', () => {
      if (!preloaded) { new Image().src = b; preloaded = true; }
      img.src = b;
    });
    tile.addEventListener('mouseleave', () => { img.src = a; });
  });

  // ---------------------------------------------------------------- lightbox
  const dialog = $('.bc-lightbox');
  const tiles = $$('.bc-wild-tile');
  if (dialog && tiles.length) {
    const lbMedia = dialog.querySelector('.bc-lb-media'), lbCap = dialog.querySelector('.bc-lb-cap');
    let lbIndex = 0, lbToken = 0;
    const showSlide = i => {
      lbIndex = (i + tiles.length) % tiles.length;
      const t = tiles[lbIndex];
      const cap = t.querySelector('.bc-cap').textContent;
      lbMedia.replaceChildren();
      if ('video' in t.dataset) {
        const v = document.createElement('video');
        Object.assign(v, { muted: true, loop: true, playsInline: true, autoplay: !reduceMotion, controls: true });
        v.poster = t.dataset.poster;
        v.setAttribute('aria-label', cap);
        // same sources (and URLs) as the tile's own video, so an already fetched file is reused
        const own = [...t.querySelectorAll('video source')].map(s => [s.dataset.src || s.getAttribute('src'), s.type, s.media]);
        const list = own.length ? own : [[t.dataset.webm, 'video/webm', ''], [t.dataset.mp4, 'video/mp4', '']];
        for (const [src, type, media] of list) {
          const s = document.createElement('source'); s.src = src; s.type = type; if (media) s.media = media; v.append(s);
        }
        lbMedia.append(v);
      } else {
        // show the tile's already-loaded picture at once, then upgrade to the large one once decoded
        const src = t.querySelector('img');
        const img = new Image();
        img.src = src.currentSrc || src.src; img.alt = src.alt;
        img.width = src.width; img.height = src.height;
        lbMedia.append(img);
        const id = ++lbToken;
        warm(t.dataset.src, src.srcset, '90vw').then(big => {
          if (id !== lbToken || !img.isConnected) return;
          img.srcset = src.srcset || ''; img.sizes = '90vw'; img.src = big.currentSrc || t.dataset.src;
        });
      }
      lbCap.textContent = `${cap} · ${lbIndex + 1} / ${tiles.length}`;
    };
    tiles.forEach((t, i) => t.addEventListener('click', () => {
      showSlide(i);
      dialog.showModal();
      dialog.querySelector('.bc-lb-close').focus();
      $$('video[data-bc-hoverplay]').forEach(v => v.pause());
    }));
    dialog.addEventListener('click', e => {
      if (e.target.closest('.bc-lb-prev')) return showSlide(lbIndex - 1);
      if (e.target.closest('.bc-lb-next')) return showSlide(lbIndex + 1);
      if (e.target.closest('video')) return;
      dialog.close();
    });
    dialog.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); showSlide(lbIndex - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); showSlide(lbIndex + 1); }
    });
    dialog.addEventListener('close', () => { lbMedia.replaceChildren(); tiles[lbIndex].focus({ preventScroll: true }); });
  }

  // ---------------------------------------------------------------- buy: gallery
  const galMain = $('.bc-gal-main'), galImg = $('.bc-gal-img');
  const thumbs = $$('.bc-gal-thumb');
  let galToken = 0;
  const galWarm = btn => warm(btn.dataset.src, btn.dataset.srcset, galImg.sizes);
  thumbs.forEach(btn => {
    // start fetching on intent, ~100-300 ms before the click lands
    ['pointerenter', 'pointerdown', 'focus'].forEach(ev => btn.addEventListener(ev, () => galWarm(btn), { passive: true }));
    btn.addEventListener('click', async () => {
      if (btn.getAttribute('aria-pressed') === 'true') return;
      thumbs.forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
      const id = ++galToken;
      galMain.classList.add('bc-is-loading');
      await galWarm(btn);
      if (id !== galToken) return;              // a newer tap won
      galImg.width = +btn.dataset.w; galImg.height = +btn.dataset.h;
      galImg.srcset = btn.dataset.srcset || ''; galImg.src = btn.dataset.src; galImg.alt = btn.dataset.alt;
      galMain.classList.toggle('bc-is-photo', 'photo' in btn.dataset);
      galMain.classList.toggle('bc-is-shop', 'shop' in btn.dataset);
      galMain.classList.remove('bc-is-loading');
    });
  });
  // once the gallery is on screen and the page is idle, warm the first few so most taps are instant
  if (galMain) {
    const galIO = new IntersectionObserver(es => { if (!es.some(e => e.isIntersecting)) return; galIO.disconnect();
      const go = () => thumbs.slice(1, 4).forEach(galWarm);
      'requestIdleCallback' in window ? requestIdleCallback(go, { timeout: 2000 }) : setTimeout(go, 600);
    }, { rootMargin: '200px 0px' });
    galIO.observe(galMain);
  }

  // ---------------------------------------------------------------- buy: bundles + quantity -> product form
  // quantity posted to the cart = the stepper value (1-10); the bundle radios mirror it.
  // The button shows the bundle total (price x quantity minus the matching bundle discount), same as the cart.
  const panel = $('.bc-panel');
  const form = $('#bc-product-form');
  if (panel && form) {
    const price = parseInt(panel.dataset.price, 10) || 0;
    const money = moneyFormatter(panel);
    const available = panel.dataset.available === 'true';
    const buyBtn = form.querySelector('.bc-buy-btn'), qtyInput = form.querySelector('input[name="quantity"]');
    const qtyVal = form.querySelector('.bc-qty-val'), qtyBtns = [...form.querySelectorAll('.bc-qty-btn')];
    const radios = [...form.querySelectorAll('input[name="bc-bundle"]')];
    // bundle cards from the live price (same integer maths as the Liquid that rendered them)
    radios.forEach(r => {
      const n = +r.value, off = +r.dataset.discount || 0, label = r.closest('.bc-bundle');
      const each = Math.floor(price * (100 - off) / 100), total = Math.floor(price * n * (100 - off) / 100);
      label.querySelector('.bc-b-price').textContent = money(total);
      label.querySelector('.bc-b-each').textContent = `${money(each)} each`;
    });
    // Quantity and bundle are one control: quantity N selects the "N Bitcubes" bundle
    // (quantities above the largest bundle keep that bundle selected), and picking a bundle sets the quantity.
    let qty = 1;
    const maxSize = Math.max(...radios.map(r => +r.value));
    const syncRadios = () => {
      const tier = Math.min(qty, maxSize);
      radios.forEach(r => { r.checked = +r.value === tier; });
    };
    const update = () => {
      qtyInput.value = String(qty);
      qtyVal.textContent = String(qty);
      qtyBtns[0].disabled = qty <= 1;
      qtyBtns[1].disabled = qty >= 99;
      if (available) buyBtn.textContent = `Pre-order ${money(Math.floor(price * qty * (100 - (+(radios.find(r => +r.value === Math.min(qty, maxSize)) || { dataset: {} }).dataset.discount || 0)) / 100))}`;
    };
    radios.forEach(r => r.addEventListener('change', () => {
      if (!r.checked) return;
      qty = +r.value;
      update();
    }));
    qtyBtns.forEach(b => b.addEventListener('click', () => {
      qty = Math.max(1, Math.min(99, qty + Number(b.dataset.step)));
      syncRadios();
      update();
    }));
    qty = +(radios.find(r => r.checked) || { value: 1 }).value;
    update();
  }

  // ---------------------------------------------------------------- specs: open on wide screens
  const specDetails = $('.bc-spec-details');
  if (specDetails) {
    const wide = matchMedia('(min-width: 700px)');
    const syncSpecs = () => { if (wide.matches) specDetails.open = true; };
    syncSpecs();
    wide.addEventListener('change', syncSpecs);
  }

  // ---------------------------------------------------------------- the live demo
  const tryRoot = $('#bc-try');
  if (tryRoot) {
    const map = window.BITCUBE_ASSETS;
    // asset_url gives "//shop/cdn/…/bitcube-live.js?v=…"; resolve to an absolute URL so import() accepts any form
    const liveUrl = map && map['live.js'] ? new URL(map['live.js'], document.baseURI).href : new URL('./bitcube-live.js', import.meta.url).href;
    import(liveUrl).then(m => m.initLive(tryRoot)).catch(err => {
      const status = tryRoot.querySelector('.bc-live-status');
      if (status) status.textContent = 'The demo could not load.';
      console.error('[bitcube] live demo', err);
    });
  }
}

init(document.getElementById('bitcube-landing'));
// theme editor: the section is re-rendered in place when its settings change
document.addEventListener('shopify:section:load', e => init(e.target.querySelector('#bitcube-landing')));
